import { initializeApp, type FirebaseApp } from "firebase/app";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { getMessaging, getToken, onMessage, type Messaging } from "firebase/messaging";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";
import {
  initializeAppCheck,
  ReCaptchaV3Provider,
  getToken as getAppCheckToken,
  type AppCheck,
} from "firebase/app-check";
import { getRecaptchaSiteKey, getVapidKey } from "../utils";

interface FirebaseBundle {
  app: FirebaseApp;
  auth: Auth;
  messaging: Messaging | null;
  appCheck: AppCheck | null;
  isEmulatorMode: boolean;
}

let bundle: FirebaseBundle | null = null;

const initBundle = (): FirebaseBundle => {
  if (typeof window === "undefined") {
    throw new Error("Firebase is only available in the browser");
  }
  if (bundle) return bundle;

  const location = window.location.hostname;
  const isEmulatorMode = location === "localhost" || location === "127.0.0.1";

  const firebaseConfig = isEmulatorMode
    ? {
        apiKey: "demo-key",
        projectId: "corlmi-406214",
        authDomain: "localhost",
        appId: "app-id",
        messagingSenderId: "32132",
      }
    : {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
        databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        appId: process.env.REACT_APP_FIREBASE_APP_ID,
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
      };

  const app = initializeApp(firebaseConfig);
  const auth = getAuth(app);

  let messaging: Messaging | null = null;
  try {
    messaging = getMessaging(app);
  } catch (e) {
    console.warn("Messaging unavailable:", e);
  }

  let appCheck: AppCheck | null = null;
  const siteKey = getRecaptchaSiteKey();
  if (!isEmulatorMode && siteKey) {
    try {
      appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaV3Provider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    } catch (e) {
      console.warn("AppCheck init failed:", e);
    }
  }

  if (isEmulatorMode) {
    console.debug("Emulator config:", firebaseConfig);
    try {
      connectAuthEmulator(auth, "http://localhost:9099");
    } catch (e) {
      console.warn("Auth emulator connect failed:", e);
    }
  }

  bundle = { app, auth, messaging, appCheck, isEmulatorMode };
  return bundle;
};

let fcmToken: string | null = null;

export const initializeFCM = async (): Promise<string | null> => {
  try {
    const { messaging } = initBundle();
    const vapidKey = getVapidKey();
    if (!messaging || !vapidKey) {
      console.log("Messaging or VAPID key not configured");
      return null;
    }
    const token = await getToken(messaging, { vapidKey });
    if (token) {
      fcmToken = token;
      return token;
    }
    return null;
  } catch (err) {
    console.log("Error retrieving FCM token:", err);
    return null;
  }
};

export const getFCMToken = (): string | null => fcmToken;

export const onMessageListener = () =>
  new Promise((resolve) => {
    try {
      const { messaging } = initBundle();
      if (!messaging) return;
      onMessage(messaging, (payload) => {
        console.log("Message received.", payload);
        resolve(payload);
      });
    } catch (e) {
      console.warn("onMessageListener error:", e);
    }
  });

export const getAppInstance = () => initBundle().app;
export const getAuthInstance = () => initBundle().auth;

export const fetchAppCheckToken = async (): Promise<string> => {
  try {
    const { appCheck, isEmulatorMode } = initBundle();
    if (isEmulatorMode) return "";
    if (!appCheck) throw new Error("App Check not initialized");
    const token = await getAppCheckToken(appCheck);
    return token.token;
  } catch (error) {
    console.error("Error getting App Check token:", error);
    throw error;
  }
};

export enum CallStatus {
  DIALING = "Dialing",
  RINGING = "ringing",
  ACTIVE = "active",
  BUSY = "busy",
  REJECTED = "rejected",
  CANCELED = "canceled",
  ABANDONED = "abandoned",
  ENDED = "ended",
}

/**
 * Callbacks for handling real-time Firestore call status updates.
 */
export interface CallStatusListenerCallbacks {
  /**
   * Triggered when the receiver requests an Agora token (status transitions to "active").
   * Indicates the receiver picked up the call and the caller should now join the Agora channel.
   */
  onActive: () => void;

  /**
   * Triggered when the call transitions to a terminal failure or end state
   * (e.g. "rejected", "busy", "canceled", "abandoned", "ended").
   * Used to stop the ringtone, clean up resources, and present the termination reason to the caller.
   */
  onCallFailed: (status: CallStatus) => void;

  /**
   * Optional hook triggered on every snapshot update from Firestore.
   * Useful for debugging, telemetry, or tracking intermediate state transitions (e.g. "Dialing" -> "ringing").
   */
  onStatusChange?: (status: string, data?: any) => void;
}

/**
 * Listens to real-time status changes for an ongoing call in Firestore.
 *
 * @param channel The unique Agora channel name / Firestore document ID
 * @param callbacks Callbacks to handle active connection, terminal failures, and status updates
 * @returns Unsubscribe function to detach the Firestore snapshot listener
 */
export const setupFireStoreListener = (
  channel: string,
  callbacks: CallStatusListenerCallbacks
): (() => void) => {
  const db = getFirestore(getAppInstance());
  const docRef = doc(db, "ongoing_calls", channel);

  let hasTriggeredActive = false;

  const unsubscribe = onSnapshot(
    docRef,
    (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const status = data?.status;
        console.log("Call status update:", data);

        if (callbacks.onStatusChange) {
          callbacks.onStatusChange(status, data);
        }

        if (status === CallStatus.ACTIVE && !hasTriggeredActive) {
          hasTriggeredActive = true;
          callbacks.onActive();
        } else if (
          status === CallStatus.REJECTED ||
          status === CallStatus.BUSY ||
          status === CallStatus.CANCELED ||
          status === CallStatus.ABANDONED ||
          status === CallStatus.ENDED
        ) {
          callbacks.onCallFailed(status as CallStatus);
          unsubscribe();
        }
      }
    },
    (error) => console.error("Error listening to call status:", error),
  );

  return unsubscribe;
};

